import { theme } from '@traba/theme'
import { ReactNode } from 'react'
import styled from 'styled-components'

export type StepTypeOptionAccent =
  | 'violet'
  | 'green'
  | 'orange'
  | 'blue'
  | 'red'

const ACCENT_COLORS: Record<
  StepTypeOptionAccent,
  { color: string; background: string }
> = {
  violet: {
    color: theme.colors.Violet60,
    background: theme.colors.Violet10,
  },
  green: {
    color: theme.colors.Green70,
    background: theme.colors.Green10,
  },
  orange: {
    color: theme.colors.Yellow70,
    background: theme.colors.Yellow10,
  },
  blue: {
    color: theme.colors.Blue80,
    background: theme.colors.Blue10,
  },
  red: {
    color: theme.colors.Red70,
    background: theme.colors.Red10,
  },
}

export interface StepTypeOptionProps {
  /** Color accent for the icon background + tile. */
  accent?: StepTypeOptionAccent
  /** Icon node — typically an SvgIcon. Rendered inside an accent-colored tile. */
  icon: ReactNode
  title: string
  description: string
  onClick: () => void
}

/**
 * Picker card for choosing a typed option (e.g. "Interview", "Profile
 * picture", "Custom") in a grid. Hover state darkens the border to violet
 * and tints the background. Used in the create-flow wizard step picker
 * and reusable for any "pick one of N typed actions" UX.
 */
export function StepTypeOption({
  accent = 'violet',
  icon,
  title,
  description,
  onClick,
}: StepTypeOptionProps) {
  const a = ACCENT_COLORS[accent]
  return (
    <Container onClick={onClick}>
      <Tile $color={a.color} $background={a.background}>
        {icon}
      </Tile>
      <TextCol>
        <Title>{title}</Title>
        <Description>{description}</Description>
      </TextCol>
    </Container>
  )
}

const Container = styled.button`
  display: flex;
  gap: ${theme.space.xs}px;
  align-items: flex-start;
  padding: ${theme.space.xsmed}px;
  background-color: ${theme.colors.White};
  border: 1px solid ${theme.colors.Grey20};
  border-radius: ${theme.border.radius};
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition:
    border-color 0.12s ease,
    background-color 0.12s ease;

  &:hover {
    border-color: ${theme.colors.Violet60};
    background-color: ${theme.colors.Violet10};
  }
`

const Tile = styled.span<{ $color: string; $background: string }>`
  width: 32px;
  height: 32px;
  border-radius: ${theme.border.radius};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: ${({ $background }) => $background};
  color: ${({ $color }) => $color};
`

const TextCol = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const Title = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${theme.colors.MidnightBlue};
`

const Description = styled.span`
  font-size: 11px;
  color: ${theme.colors.Grey60};
  line-height: 1.4;
`
